import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash, Plus } from "@phosphor-icons/react";

export default function Categories() {
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const canManage = ["admin", "manager"].includes(user?.role);
  const canDelete = user?.role === "admin";

  const load = async () => {
    const r = await api.get("/categories");
    setCats(r.data);
  };

  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name, description });
      setName(""); setDescription("");
      toast.success("Category added");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await api.delete(`/categories/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="categories-page">
      <div>
        <div className="label-caps">Taxonomy</div>
        <h2 className="font-display text-3xl text-navy uppercase mt-1">Categories</h2>
      </div>

      {canManage && (
        <form onSubmit={add} className="card-flat p-5 grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} data-testid="cat-name-input" onChange={(e) => setName(e.target.value)} required placeholder="Quality Certificate" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input value={description} data-testid="cat-desc-input" onChange={(e) => setDescription(e.target.value)} placeholder="ISO / compliance" />
          </div>
          <button type="submit" data-testid="add-category" className="btn-navy flex items-center gap-2">
            <Plus size={14} /> Add
          </button>
        </form>
      )}

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-zinc-200 bg-zinc-50">
              <th className="px-4 py-3 label-caps">Name</th>
              <th className="px-4 py-3 label-caps">Description</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.id} className="dms-row border-b border-zinc-100" data-testid={`cat-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{c.name}</td>
                <td className="px-4 py-3 text-zinc-700">{c.description || "—"}</td>
                <td className="px-4 py-3 text-right">
                  {canDelete && (
                    <button
                      onClick={() => del(c.id)}
                      data-testid={`delete-cat-${c.id}`}
                      className="text-zinc-400 hover:text-red-600 transition-colors"
                    >
                      <Trash size={16} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
